import {
  LeadResearchCostRun,
  useApolloQuota,
  useHubSpotStatus,
  useLandlordResearchTrace,
  useLeadResearchCosts,
  useHubSpotMatchTrace,
  useOutreachTrace,
  usePeopleDiscoveryTrace,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  OverlayTrigger,
  Table,
  Tooltip,
} from "react-bootstrap";
import CollapsibleCard from "../site/CollapsibleCard";

const usd = (n: number): string => `$${n.toFixed(2)}`;

// Runtime column: sub-minute shows whole seconds, a minute+ shows "Xm Ys".
const fmtRuntime = (seconds: number | null): string => {
  if (seconds == null) return "-";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

// Apollo's free daily discovery quota resets at 00:00 UTC. Show how long until then
// so an operator who's hit the cap knows when it tops back up.
const apolloResetHint = (): string => {
  const now = new Date();
  const nextMidnightUtc = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1
  );
  const msLeft = nextMidnightUtc - now.getTime();
  const hours = Math.floor(msLeft / 3_600_000);
  const mins = Math.floor((msLeft % 3_600_000) / 60_000);
  const rel = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  return `resets in ${rel} (00:00 UTC)`;
};

// The AI-invocation ledger `source` value for contacts-ranking spend (mirror of
// the BE `PEOPLE_DISCOVERY_COST_SOURCE` in operation_people_discovery.py). A run
// tagged with this routes its trace link to the contacts-ranking trace endpoint;
// everything else (landlord research) uses the landlord-research trace endpoint.
const CONTACTS_RANKING_SOURCE = "Contacts ranking";
// The ledger `source` for cold-outreach drafting spend (mirror of the BE
// OUTREACH_COST_SOURCE in outreach.py). Its trace lives on the outreach endpoint,
// keyed by the outreach message id (traceSubjectRef) + trace run id.
const OUTREACH_SOURCE = "Outreach drafting";
// The ledger `source` for HubSpot-matching spend (mirror of the BE
// HUBSPOT_MATCH_COST_SOURCE in hubspot_match.py). Its trace is role-scoped like the
// contacts-ranking one, but stored under its own prefix.
const HUBSPOT_MATCH_SOURCE = "HubSpot matching";

const LeadResearchCostsCard = () => {
  const [days, setDays] = useState<number>(30);
  const costs = useLeadResearchCosts(days);
  const quota = useApolloQuota();
  const hubspot = useHubSpotStatus();
  const [traceOf, setTraceOf] = useState<LeadResearchCostRun | null>(null);

  // The trace lives on a different endpoint per surface, so call every hook
  // (hooks can't be conditional) and enable only the one matching the run's
  // source; render whichever is enabled.
  const isContactsTrace = traceOf?.source === CONTACTS_RANKING_SOURCE;
  const isOutreachTrace = traceOf?.source === OUTREACH_SOURCE;
  const isHubSpotTrace = traceOf?.source === HUBSPOT_MATCH_SOURCE;
  const landlordTrace = useLandlordResearchTrace(
    traceOf?.traceSubjectRef ?? null,
    traceOf?.traceRunId ?? null,
    traceOf !== null && !isContactsTrace && !isOutreachTrace && !isHubSpotTrace
  );
  const peopleTrace = usePeopleDiscoveryTrace(
    traceOf?.traceSubjectRef ?? null,
    traceOf?.traceRunId ?? null,
    traceOf !== null && isContactsTrace
  );
  const outreachTrace = useOutreachTrace(
    traceOf?.traceSubjectRef ?? null,
    traceOf?.traceRunId ?? null,
    traceOf !== null && isOutreachTrace
  );
  const hubspotTrace = useHubSpotMatchTrace(
    traceOf?.traceSubjectRef ?? null,
    traceOf?.traceRunId ?? null,
    traceOf !== null && isHubSpotTrace
  );
  const traceQuery = isHubSpotTrace
    ? hubspotTrace
    : isOutreachTrace
      ? outreachTrace
      : isContactsTrace
        ? peopleTrace
        : landlordTrace;

  const apollo = quota.data;
  // Guard against an older BE (deploy skew: frontends ahead of building-manager-api)
  // that predates `bySource` - Object.entries(undefined) would throw and take the
  // whole card down rather than degrading to "no per-source split".
  const sourceRows = costs.data?.bySource
    ? Object.entries(costs.data.bySource).filter(([, b]) => b.runCount > 0)
    : [];

  return (
    <CollapsibleCard title="Leads costs & API usage" initialOpen={false}>
      <div className="d-flex flex-column gap-3" style={{ fontSize: 13 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Form.Label style={{ margin: 0, fontSize: 12, color: "#888" }}>
            Window
          </Form.Label>
          <Form.Select
            size="sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ width: 160 }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </Form.Select>
          <span style={{ fontSize: 11, color: "#aaa" }}>
            Estimated from token + web-search counts (not billed).
          </span>
        </div>

        {/* Apollo free-tier daily discovery quota ------------------------- */}
        <div style={{ fontSize: 12 }}>
          {quota.isLoading ? (
            <span style={{ color: "#888" }}>Loading Apollo quota...</span>
          ) : !apollo ? (
            <span style={{ color: "#aaa" }}>Apollo quota unavailable.</span>
          ) : !apollo.configured ? (
            <span style={{ color: "#b8860b" }}>
              Apollo People Search is not configured on this environment - the
              &quot;Find people&quot; actions will not run.
            </span>
          ) : apollo.dayLimit != null ? (
            <span>
              <strong>Apollo discovery today:</strong> {apollo.dayConsumed ?? 0}{" "}
              / {apollo.dayLimit} calls used
              {apollo.dayLeft != null && (
                <span style={{ color: "#888" }}> ({apollo.dayLeft} left)</span>
              )}
              <span style={{ color: "#aaa" }}>
                {" "}
                - free discovery, 0 credits
              </span>
              <span style={{ color: "#888" }}> - {apolloResetHint()}</span>
            </span>
          ) : (
            <span style={{ color: "#aaa" }}>
              Apollo quota unavailable
              {apollo.error ? ` (${apollo.error})` : ""}.
            </span>
          )}
        </div>

        {/* Apollo PAID enrichment credits (contact reveals) - separate from the
            free discovery calls above. Balance is null when Apollo doesn't expose
            it on this plan; then we show a hint rather than a fabricated number. */}
        {apollo && apollo.configured && (
          <div style={{ fontSize: 12 }}>
            {apollo.enrichmentCredits ? (
              <span>
                <strong>Apollo enrichment:</strong>{" "}
                {apollo.enrichmentCredits.used} credit
                {apollo.enrichmentCredits.used === 1 ? "" : "s"} used
                <span style={{ color: "#888" }}>
                  {" "}
                  ({apollo.enrichmentCredits.left} remaining
                  {apollo.enrichmentCredits.limit
                    ? ` of ${apollo.enrichmentCredits.limit}`
                    : ""}
                  )
                </span>
                <span style={{ color: "#aaa" }}>
                  {" "}
                  - paid, ~1 credit per contact revealed
                </span>
              </span>
            ) : (
              <span style={{ color: "#aaa" }}>
                <strong style={{ color: "#888" }}>Apollo enrichment:</strong>{" "}
                credit balance unavailable on this plan.
              </span>
            )}
          </div>
        )}

        {/* HubSpot connection. Deliberately NOT a quota line: reads are free and the
            daily cap sits far above our use, so a usage figure would only dilute
            Apollo's genuinely scarce 600/day. This answers the question the card
            could not - whether the integration works at all - because a dead token
            was previously visible only in the worker's logs. */}
        <div style={{ fontSize: 12 }}>
          {hubspot.isLoading ? (
            <span style={{ color: "#888" }}>Checking HubSpot...</span>
          ) : !hubspot.data ? (
            <span style={{ color: "#aaa" }}>HubSpot status unavailable.</span>
          ) : hubspot.data.status === "connected" ? (
            <span>
              <strong>HubSpot:</strong>{" "}
              <span style={{ color: "#0f5132" }}>connected</span>
              {hubspot.data.portalId && (
                <span style={{ color: "#888" }}>
                  {" "}
                  - portal {hubspot.data.portalId}
                </span>
              )}
              <span style={{ color: "#aaa" }}> - reads are free</span>
            </span>
          ) : (
            <span>
              <strong>HubSpot:</strong>{" "}
              <span style={{ color: "#b8860b" }}>
                {hubspot.data.status === "not_configured"
                  ? "not connected"
                  : "unreachable"}
              </span>
              {hubspot.data.detail && (
                <span style={{ color: "#888" }}> - {hubspot.data.detail}</span>
              )}
            </span>
          )}
        </div>

        {costs.isLoading ? (
          <div style={{ color: "#888" }}>Loading costs...</div>
        ) : costs.isError || !costs.data ? (
          <Alert variant="danger">Could not load lead costs.</Alert>
        ) : (
          <>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {usd(costs.data.totals.totalUsd)}
                </div>
                <div style={{ fontSize: 11, color: "#888" }}>
                  total ({costs.data.totals.runCount} runs,{" "}
                  {costs.data.totals.testRunCount} test)
                </div>
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {usd(costs.data.totals.modelUsd)}
                </div>
                <div style={{ fontSize: 11, color: "#888" }}>model tokens</div>
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {usd(costs.data.totals.webSearchUsd)}
                </div>
                <div style={{ fontSize: 11, color: "#888" }}>
                  web search ({costs.data.totals.webSearchCount} queries @{" "}
                  {usd(costs.data.webSearchUsdPerQuery)})
                </div>
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {costs.data.totals.apolloCredits ?? 0}
                </div>
                {/* Apollo enrichment spends a non-USD credit budget the totalUsd above
                    deliberately omits, so show the credit count as its own figure. */}
                <div style={{ fontSize: 11, color: "#888" }}>
                  Apollo credits
                </div>
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {costs.data.totals.apolloSearchCalls ?? 0}
                </div>
                {/* Apollo people-discovery calls: free but daily-capped, so show the call
                    count so discovery activity reads as work done, not a $0 no-op. */}
                <div style={{ fontSize: 11, color: "#888" }}>
                  Apollo searches
                </div>
              </div>
            </div>

            {sourceRows.length > 0 && (
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {sourceRows.map(([label, b]) => {
                  // apolloCredits / apolloSearchCalls can be absent on an older BE (deploy
                  // skew); treat a missing value as 0 so the row still renders rather than
                  // blank.
                  const credits = b.apolloCredits ?? 0;
                  const searches = b.apolloSearchCalls ?? 0;
                  return (
                    <div key={label} style={{ fontSize: 12 }}>
                      <span style={{ color: "#888" }}>{label}:</span>{" "}
                      <strong>
                        {/* Enrichment spends Apollo credits and discovery spends free
                            Apollo calls, neither in USD - show each count for the source
                            that has it and $ for the model-token sources. */}
                        {[
                          b.totalUsd > 0 || (credits === 0 && searches === 0)
                            ? usd(b.totalUsd)
                            : null,
                          credits > 0
                            ? `${credits} credit${credits === 1 ? "" : "s"}`
                            : null,
                          searches > 0
                            ? `${searches} search${searches === 1 ? "" : "es"}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </strong>{" "}
                      <span style={{ color: "#aaa" }}>
                        ({b.runCount} run{b.runCount === 1 ? "" : "s"})
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {costs.data.recent.length === 0 ? (
              <div style={{ fontSize: 12, color: "#666" }}>
                No lead-research runs in this window.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table size="sm" striped hover style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Source</th>
                      <th>Subject</th>
                      <th style={{ textAlign: "right" }}>Web</th>
                      <th style={{ textAlign: "right" }}>Apollo calls</th>
                      <th style={{ textAlign: "right" }}>Credits</th>
                      <th style={{ textAlign: "right" }}>Runtime</th>
                      <th style={{ textAlign: "right" }}>Cost</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {costs.data.recent.map((r) => (
                      <tr key={r.id}>
                        <td>{new Date(r.createdAt).toLocaleString()}</td>
                        <td>{r.source ?? "-"}</td>
                        <td>
                          {/* Show the lead's address; the raw ref (UUID / VOA ref)
                              stays on hover for debugging. Falls back to the ref
                              when the lead can't be resolved. */}
                          <span title={r.subjectRef ?? undefined}>
                            {r.subjectLabel ?? r.subjectRef ?? "-"}
                          </span>
                          {r.subjectRole && (
                            <Badge
                              bg="light"
                              text="dark"
                              style={{ marginLeft: 4, fontWeight: 500 }}
                            >
                              {r.subjectRole === "tenant"
                                ? "Tenant"
                                : "Landlord"}
                            </Badge>
                          )}
                          {r.isTest && (
                            <Badge bg="info" style={{ marginLeft: 4 }}>
                              test
                            </Badge>
                          )}
                          {r.failed &&
                            (r.error ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip
                                    style={{
                                      whiteSpace: "pre-line",
                                      textAlign: "left",
                                    }}
                                  >
                                    {r.error}
                                  </Tooltip>
                                }
                              >
                                <Badge bg="danger" style={{ marginLeft: 4 }}>
                                  failed
                                </Badge>
                              </OverlayTrigger>
                            ) : (
                              <Badge bg="secondary" style={{ marginLeft: 4 }}>
                                failed
                              </Badge>
                            ))}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {r.webSearchCount}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {r.apolloSearchCalls != null
                            ? r.apolloSearchCalls
                            : "-"}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {r.apolloCredits != null ? r.apolloCredits : "-"}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {fmtRuntime(r.durationSeconds)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {usd(r.totalUsd)}
                        </td>
                        <td>
                          {r.traceRunId && (
                            <Button
                              size="sm"
                              variant="link"
                              style={{ fontSize: 11, padding: 0 }}
                              onClick={() => setTraceOf(r)}
                            >
                              trace
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {costs.data.truncated && (
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    Showing the most recent {costs.data.recent.length} runs;
                    totals above cover the full window.
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <Modal
          show={traceOf !== null}
          onHide={() => setTraceOf(null)}
          size="xl"
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 18 }}>
              {isOutreachTrace
                ? "Outreach drafting"
                : isContactsTrace
                  ? "Contacts ranking"
                  : "Research"}{" "}
              run - AI trace
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {traceQuery.isLoading ? (
              <div style={{ fontSize: 12, color: "#888" }}>
                Loading trace...
              </div>
            ) : traceQuery.data ? (
              <AITraceViewer trace={traceQuery.data} />
            ) : (
              <div style={{ fontSize: 12, color: "#888" }}>
                No AI trace available for this run.
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </CollapsibleCard>
  );
};

export default LeadResearchCostsCard;
